import {
  RESIDENCE_PERMIT_CONFIG,
  type ResidencePermitContext,
  type ResidencePermitData,
  type ResidencePermitExtraction,
} from "@asap/shared/document/document-types/residence-permit";
import { ResidencePermitSchemas } from "@asap/shared/schemas/document/document-types/residence-permit";
import { countryOptions, nationalityOptions } from "@asap/shared/utils/countries";
import { formatFirstName, formatLastName } from "@asap/shared/utils/string";
import { importAsset } from "~/utils/asset";
import { DocumentTypeHandler } from "../handler";

/**
 * Residence Permit Handler (Titre de séjour)
 */
export const ResidencePermitHandler = new DocumentTypeHandler<
  ResidencePermitExtraction,
  ResidencePermitData,
  ResidencePermitContext
>({
  // Identity
  documentType: RESIDENCE_PERMIT_CONFIG.documentType,
  documentName: RESIDENCE_PERMIT_CONFIG.documentName,

  // Constraints
  maxFiles: RESIDENCE_PERMIT_CONFIG.maxFiles,
  acceptedMimeTypes: RESIDENCE_PERMIT_CONFIG.acceptedMimeTypes,
  requiredFields: RESIDENCE_PERMIT_CONFIG.requiredFields,

  // API endpoints
  endpoints: {
    extract: "documents/residence-permit/extract",
    warn: "documents/residence-permit/warn",
    verify: "documents/residence-permit/verify",
    store: "documents/residence-permit/store",
  },

  // Response schemas for runtime validation
  schemas: {
    extractResponse: ResidencePermitSchemas.extractResponse,
    warnResponse: ResidencePermitSchemas.warnResponse,
    validateResponse: ResidencePermitSchemas.validateResponse,
    storeResponse: ResidencePermitSchemas.storeResponse,
  },

  // Banner shown at the top of the verification dialog
  banner: {
    illustrationSrc: importAsset("images/document-banners/contract-lifecycle.svg"),
    title: "Que se passe-t-il après la validation du titre ?",
    text: "Un email est automatiquement envoyé à la préfecture compétente pour vérification du document. Compte jusqu'à 48 h avant la réponse.",
  },

  // Toast messages
  toastMessages: {
    success: "Le titre de séjour a bien été validé.",
    externalReviewStarted: "Le titre de séjour a été envoyé pour validation à la préfecture de police.",
    externalReviewFailed:
      "Une erreur est survenue lors de l'envoi pour validation à la préfecture de police. Merci de contacter l'équipe Product & Tech.",
  },

  // Form fields
  formFields: [
    {
      key: "last_name",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.last_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "first_name",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.first_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "sex",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.sex,
      type: "select",
      options: [
        { value: "male", label: "Masculin" },
        { value: "female", label: "Féminin" },
        { value: "neutral", label: "Neutre" },
      ],
      isSearchable: false,
      colSpan: 1,
    },
    {
      key: "document_number",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.document_number,
      type: "text",
      placeholder: "Ex : 123456789",
      colSpan: 1,
    },
    {
      key: "expiration_date",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.expiration_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "issue_date",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.issue_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "birth_date",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.birth_date,
      type: "date",
    },
    {
      key: "birth_place_city",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.birth_place_city,
      type: "text",
      placeholder: "Ex : Alger",
    },
    {
      key: "birth_place_country_code",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.birth_place_country_code,
      type: "select",
      options: countryOptions(),
    },
    {
      key: "nationality_country_code",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.nationality_country_code,
      type: "select",
      options: nationalityOptions(),
    },
    {
      key: "issuing_country_code",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.issuing_country_code,
      type: "select",
      options: countryOptions(),
    },
    {
      key: "residence_permit_type",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.residence_permit_type,
      type: "select",
      options: [
        { value: "titre_sejour_temporaire", label: "Titre de séjour temporaire" },
        { value: "carte_resident", label: "Carte de résident" },
        { value: "carte_sejour_pluriannuelle", label: "Carte de séjour pluriannuelle" },
        { value: "carte_competences_et_talents", label: "Carte compétences et talents" },
        { value: "carte_membre_famille_ue", label: "Carte membre famille UE" },
        { value: "carte_mtp", label: "Carte MTP" },
        { value: "carte_aps", label: "Carte APS" },
        { value: "carte_recepissee", label: "Récépissé" },
        { value: "titre_de_voyage", label: "Titre de voyage" },
        { value: "titre_d_identite_republicain", label: "Titre d'identité républicain" },
        { value: "dceme", label: "DCEM/DCEME" },
        { value: "titre_de_travail", label: "Titre de travail" },
        { value: "autre", label: "Autre" },
      ],
      isSearchable: true,
    },
    {
      key: "is_student_status",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.is_student_status,
      type: "radio",
      options: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
    {
      key: "requires_original_permit",
      label: RESIDENCE_PERMIT_CONFIG.fieldLabels.requires_original_permit,
      type: "radio",
      options: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
  ],

  // Transform OCR extraction to form data
  transformExtraction: (extraction: ResidencePermitExtraction): Partial<ResidencePermitData> => {
    // Validation (throws on issues)
    if (!extraction.isValidDocumentType) {
      throw new Error(
        "Les fichiers ajoutés ne semblent pas correspondre à un titre de séjour. Vérifie que ce soit bien le cas."
      );
    }

    // Transformation (only non-null values)
    const result: Partial<ResidencePermitData> = {};

    if (extraction.first_name) result.first_name = formatFirstName(extraction.first_name);
    if (extraction.last_name) result.last_name = formatLastName(extraction.last_name);
    if (extraction.birth_date) result.birth_date = extraction.birth_date;
    if (extraction.birth_place_city) result.birth_place_city = extraction.birth_place_city;
    if (extraction.birth_place_country_code) result.birth_place_country_code = extraction.birth_place_country_code;
    if (extraction.sex) result.sex = extraction.sex;
    if (extraction.nationality_country_code) result.nationality_country_code = extraction.nationality_country_code;
    if (extraction.issuing_country_code) result.issuing_country_code = extraction.issuing_country_code;
    if (extraction.issue_date) result.issue_date = extraction.issue_date;
    if (extraction.expiration_date) result.expiration_date = extraction.expiration_date;
    if (extraction.document_number) result.document_number = extraction.document_number;
    if (extraction.is_student_status !== null) result.is_student_status = extraction.is_student_status;
    if (extraction.residence_permit_type) result.residence_permit_type = extraction.residence_permit_type;
    if (extraction.requires_original_permit !== null)
      result.requires_original_permit = extraction.requires_original_permit;

    return result;
  },
});
